/**
 * Clarity chart — C50 (solid) and C80 (dashed) overlaid vs. frequency.
 * Paint layer only — see `../clarity.ts` for the ISO 3382 metric math and
 * `../clarityChart.ts` for the geometry. Same dash-not-colour convention as
 * the decay-fit comparison chart. Ports web's
 * `web/src/components/room/charts/clarityDraw.ts` dash maps and legend
 * layout, swapping its dark-dock ink for mobile's `INK.primary`.
 */

import { canvasTypeFont } from '@roomtreatment/design';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK } from './_shared';
import { computeClarityFits } from '../clarity';
import { computeClarityChartGeometry, type ClarityMetric } from '../clarityChart';
import type { ChartPad } from '../layout';
import type { RT60DecayCurve } from '../registry';

export const CLARITY_PAD: ChartPad = { t: 10, r: 14, b: 20, l: 34 };

const METHOD_LABEL: Record<ClarityMetric, string> = { c50: 'C50', c80: 'C80' };
export const METHOD_DASH: Record<ClarityMetric, number[]> = { c50: [], c80: [6, 3] };
export const METHOD_WIDTH: Record<ClarityMetric, number> = { c50: 2.0, c80: 1.4 };

export interface DrawClarityOpts {
  decay: RT60DecayCurve;
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawClarityCanvas(canvas: HTMLCanvasElement, opts: DrawClarityOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const fits = computeClarityFits(opts.decay);
  const geo = computeClarityChartGeometry({
    fits,
    width: opts.width,
    height: opts.height,
    pad: CLARITY_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No decay curve yet — ≥200 Hz only');
    return;
  }

  // Only x stays flat (no major/minor split); y keeps its major/minor grid
  // — matches web's clarityDraw.ts, not a bug to "fix".
  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks, { xDistinguishMajor: false });

  for (const series of geo.series) {
    drawSeriesLine(ctx, geo.frame, series.points, INK.primary, METHOD_WIDTH[series.metric], METHOD_DASH[series.metric]);
  }

  ctx.font = canvasTypeFont('label');
  let lx = geo.frame.plotX + 6;
  const ly = geo.frame.plotY + 8;
  for (const metric of ['c50', 'c80'] as const) {
    ctx.setLineDash(METHOD_DASH[metric]);
    ctx.strokeStyle = INK.primary;
    ctx.lineWidth = METHOD_WIDTH[metric];
    ctx.beginPath();
    ctx.moveTo(lx, ly);
    ctx.lineTo(lx + 14, ly);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = INK.label;
    ctx.textAlign = 'left';
    ctx.fillText(METHOD_LABEL[metric], lx + 17, ly + 3);
    lx += 17 + ctx.measureText(METHOD_LABEL[metric]).width + 10;
  }
}
